/**
 * Хто може відкрити нашу адмінку — головний адміністратор, адміністратор організації або
 * адміністратор області (ОВА, лише огляд громад своєї області, [[rol-administratora-ova]]).
 *
 * Роль питаємо в automation (`/whoami`), бо в клієнта Grist ознаки «адмін організації» немає
 * взагалі: вона живе в `Sys_user_attributes` документа, а `RegionalCapabilities.role` розрізняє
 * тільки regional_admin і regional_user. А на головній сторінці документа немає зовсім.
 *
 * Це НЕ захист: справжня межа стоїть на ендпоінтах адмін-АРІ. Тут лише не показуємо вхід тому,
 * кому він усе одно відмовить. Будь-яка невдача (не той користувач, automation недоступна)
 * просто лишає вхід схованим.
 *
 * Одне місце для двох входів — у «Інструментах» документа й на лівій панелі головної («одні
 * двері», [[obiednannia-adminok]]): два окремі запити розійшлися б, щойно правило зміниться.
 *
 * Той самий `/whoami` вирішує, яку настанову відкриває посилання «Інструкції»
 * ([[instruktsii-posylanniam-i-bez-videotura-grist]]). Запит один на сторінку: для всіх, крім
 * головного адміна, automation тягне на нього користувачів із Keycloak, тож повторювати його на
 * кожній панелі — зайве навантаження.
 */
import { IDisposableOwner, Observable } from "grainjs";

export const REGIONAL_ADMIN_URL = "/api/regional/admin/ui/";

/** Настанови віддає automation через проксі Grist; вхід потрібен, роль — ні. */
export const REGIONAL_HANDBOOK_URL = "/api/regional/handbook/";

const GUIDE_PAGES = {
  mainAdmin: "nastanova-holovnoho-administratora.html",
  orgAdmin: "nastanova-administratora-hromady.html",
  oblastAdmin: "nastanova-administratora-oblasti.html",
  user: "nastanova-korystuvacha.html",
};

interface RegionalWhoami {
  main_admin?: boolean;
  org_admin?: boolean;
  oblast_admin?: boolean;
}

let _whoami: Promise<RegionalWhoami | null> | null = null;

function regionalWhoami(): Promise<RegionalWhoami | null> {
  if (!_whoami) {
    const pending = (async () => {
      try {
        const response = await fetch("/api/regional/admin/api/whoami", { credentials: "include" });
        return response.ok ? await response.json() as RegionalWhoami : null;
      } catch (err) {
        return null;   // навмисно тихо: відсутній вхід — не помилка роботи з редактором
      }
    })();
    _whoami = pending;
    // Невдачу не запам'ятовуємо: automation могла перезапускатись, наступна панель спитає ще раз.
    void pending.then((who) => { if (!who && _whoami === pending) { _whoami = null; } });
  }
  return _whoami;
}

function whoamiFlag(owner: IDisposableOwner | null, test: (who: RegionalWhoami) => boolean): Observable<boolean> {
  const allowed = Observable.create<boolean>(owner, false);
  void regionalWhoami().then((who) => {
    if (who && !allowed.isDisposed()) {
      allowed.set(test(who));
    }
  });
  return allowed;
}

/** Вхід в адмінку: головний адмін, адмін громади, адмін області. */
export function regionalAdminAccess(owner: IDisposableOwner | null): Observable<boolean> {
  return whoamiFlag(owner, who => Boolean(who.main_admin || who.org_admin || who.oblast_admin));
}

/**
 * Публікація на портал — справа громади: головний адмін або адмін громади. Адміністратор області
 * лише переглядає, тож пункту «Оприлюднити» не бачить (сервер усе одно відмовив би).
 */
export function regionalPublishAccess(owner: IDisposableOwner | null): Observable<boolean> {
  return whoamiFlag(owner, who => Boolean(who.main_admin || who.org_admin));
}

/**
 * Настанова для ролі людини. Головного адміна перевіряємо першим: у нього `org_admin` теж істинне.
 * Поки відповіді немає (або вона не прийшла) — настанова користувача: вона потрібна найширшому колу.
 */
export function regionalGuideHref(who: RegionalWhoami | null): string {
  const page = who?.main_admin ? GUIDE_PAGES.mainAdmin :
    who?.org_admin ? GUIDE_PAGES.orgAdmin :
      who?.oblast_admin ? GUIDE_PAGES.oblastAdmin : GUIDE_PAGES.user;
  return REGIONAL_HANDBOOK_URL + page;
}

export function regionalGuideUrl(owner: IDisposableOwner | null): Observable<string> {
  const url = Observable.create<string>(owner, regionalGuideHref(null));
  void regionalWhoami().then((who) => {
    if (!url.isDisposed()) { url.set(regionalGuideHref(who)); }
  });
  return url;
}
